<template>
  <div class="admin-create">
    <div class="page-header">
      <v-btn
        variant="text"
        color="#EA5545"
        class="back-btn"
        @click="router.back()"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Zurück
      </v-btn>
      <h1>Neuen Kunden anlegen</h1>
      <p class="subtitle">
        Erstelle einen neuen Kunden (kein Admin) für dein Unternehmen. Der Benutzername basiert auf
        deinem Firmen-Schlüssel und der Kundennummer.
      </p>
    </div>

    <v-card class="form-card" elevation="1">
      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-4">
          Benutze eine eindeutige Kundennummer. Der spätere Benutzername lautet
          <strong>{{ previewUsername }}</strong>.
          <div class="hint-key" v-if="adminKey">
            Firmen-Schlüssel: <strong>{{ adminKey }}</strong>
          </div>
        </v-alert>

        <v-text-field
          v-model="customerNumber"
          label="Kundennummer"
          placeholder="z.B. 1024"
          variant="outlined"
          :disabled="isSubmitting"
          :error="!!errorText"
          @input="errorText = ''"
        />

        <v-text-field
          v-model="vpName"
          label="Versicherte Person (Nachname)"
          placeholder="z.B. Mustermann"
          variant="outlined"
          :disabled="isSubmitting"
          class="mt-4"
        />

        <v-text-field
          v-model="vnName"
          label="Versicherungsnehmer (Name)"
          placeholder="Optional: abweichender Versicherungsnehmer"
          variant="outlined"
          :disabled="isSubmitting"
          class="mt-4"
        />

        <div class="actions">
          <v-btn
            color="#EA5545"
            variant="flat"
            :disabled="!customerNumber || isSubmitting"
            :loading="isSubmitting"
            @click="createCustomer"
          >
            <v-icon left size="18">mdi-account-plus</v-icon>
            Kunden anlegen
          </v-btn>
        </div>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>

        <v-alert
          v-if="successText"
          type="success"
          variant="tonal"
          class="mt-4"
        >
          {{ successText }}
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const router = useRouter()

const customerNumber = ref('')
const vpName = ref('')
const vnName = ref('')
const isSubmitting = ref(false)
const errorText = ref('')
const successText = ref('')
const adminKey = ref('')

const previewUsername = computed(() => {
  const key = adminKey.value || 'kunde'
  const value = customerNumber.value.replace(/\s+/g, '')
  if (!value) return `${key} + Nummer`
  return `${key}${value}`
})

onMounted(() => {
  fetchAdminKey()
})

async function fetchAdminKey() {
  try {
    const response = await baseCalls.userPost({ action: 'getcompanyadminkey' })
    if (response.data?.success && response.data?.admin_key) {
      adminKey.value = response.data.admin_key
    }
  } catch (error) {
    console.error('fetchAdminKey', error)
  }
}

async function createCustomer() {
  const number = customerNumber.value.trim()
  if (!number) {
    errorText.value = 'Bitte eine Kundennummer angeben.'
    return
  }
  isSubmitting.value = true
  errorText.value = ''
  successText.value = ''
  try {
    const response = await baseCalls.userPost({
      action: 'createcompanycustomer',
      customer_number: number,
      user_vp_name: vpName.value.trim(),
      user_vn_name: vnName.value.trim()
    })

    if (response.data?.success) {
      successText.value = `Kunde ${response.data.username} wurde angelegt. Weiterleitung folgt …`
      vpName.value = ''
      vnName.value = ''
      const targetId = response.data.user_id
      setTimeout(() => {
        if (targetId) {
          router.push({ name: 'adminuserdetail', params: { id: targetId } })
        } else if (response.data.redirect) {
          router.push(response.data.redirect)
        }
      }, 1200)
    } else {
      errorText.value = response.data?.message || 'Der Kunde konnte nicht angelegt werden.'
    }
  } catch (error) {
    console.error('createCustomer', error)
    errorText.value = 'Es ist ein unbekannter Fehler aufgetreten.'
    errorStore?.setError?.(true, 'Kunde konnte nicht angelegt werden.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminCreateCustomer.css"></style>
